import { Injectable, inject } from '@angular/core';
import { StepService } from '../step/step.service';
import { NotebookVariablesService } from '../notebook-variables/notebook-variables.service';
import { CacheService } from '../cache/cache.service';
import { AppStateService } from '../app-state/app-state.service';

@Injectable({
  providedIn: 'root'
})
export class UnsavedChangesService {
  private stepService = inject(StepService);
  private notebookVariablesService = inject(NotebookVariablesService);
  private cacheService = inject(CacheService);
  private appStateService = inject(AppStateService);

  // For testing purposes
  private forceUnsavedChanges = false;

  /**
   * Checks if there are any unsaved changes in the current notebook
   * This includes:
   * - Dirty forms in chunks
   * - Modified variables
   * - Cache modifications
   */
  hasUnsavedChanges(): boolean {
    console.log('=== Checking for unsaved changes ===');
    
    // For testing: Force unsaved changes if flag is set
    if (this.forceUnsavedChanges) {
      console.log('Force unsaved changes is enabled (testing mode)');
      return true;
    }
    
    // Check if we're in head version (only head version can have unsaved changes)
    const isHeadVersion = this.appStateService.isHeadVersion();
    console.log('Is head version:', isHeadVersion);
    
    if (!isHeadVersion) {
      console.log('Not in head version, no unsaved changes possible');
      return false;
    }

    // Check for dirty forms in chunk items
    const dirtyForms = document.querySelectorAll('form.ng-dirty');
    console.log('Dirty forms found:', dirtyForms.length);
    
    if (dirtyForms.length > 0) {
      console.log('Found dirty forms, has unsaved changes');
      return true;
    }

    // Check for autosave enabled chunks (indicating active editing)
    const chunks = (this.stepService as any).stepList || [];
    console.log('Total chunks:', chunks.length);
    
    const autoSaveChunks = chunks.filter((chunk: any) => (chunk as any).isAutoSaveEnabled);
    const invalidatedChunks = chunks.filter((chunk: any) => chunk.invalidated);
    
    console.log('Auto-save enabled chunks:', autoSaveChunks.length);
    console.log('Invalidated chunks:', invalidatedChunks.length);

    const hasActiveEditing = autoSaveChunks.length > 0 || invalidatedChunks.length > 0;

    if (hasActiveEditing) {
      console.log('Found active editing or invalidated chunks, has unsaved changes');
      return true;
    }

    // Check for dirty Angular forms more specifically
    const angularDirtyElements = document.querySelectorAll('.ng-dirty');
    console.log('Angular dirty elements:', angularDirtyElements.length);
    
    if (angularDirtyElements.length > 0) {
      console.log('Found Angular dirty elements, has unsaved changes');
      return true;
    }

    console.log('No unsaved changes detected');
    return false;
  }

  /**
   * Gets a description of what unsaved changes exist
   */
  getUnsavedChangesDescription(): string {
    if (this.forceUnsavedChanges) {
      return 'Testing mode: Forced unsaved changes';
    }

    const descriptions: string[] = [];

    const dirtyForms = document.querySelectorAll('form.ng-dirty');
    if (dirtyForms.length > 0) {
      descriptions.push(`${dirtyForms.length} chunk(s) with unsaved content`);
    }

    const chunks = (this.stepService as any).stepList || [];
    const invalidatedChunks = chunks.filter((chunk: any) => chunk.invalidated);
    if (invalidatedChunks.length > 0) {
      descriptions.push(`${invalidatedChunks.length} invalidated chunk(s)`);
    }

    const activeEditingChunks = chunks.filter((chunk: any) => (chunk as any).isAutoSaveEnabled);
    if (activeEditingChunks.length > 0) {
      descriptions.push(`${activeEditingChunks.length} chunk(s) being actively edited`);
    }

    return descriptions.length > 0 
      ? descriptions.join(', ')
      : 'Unsaved changes detected';
  }

  /**
   * For testing purposes: Force the service to detect unsaved changes
   */
  setForceUnsavedChanges(force: boolean): void {
    this.forceUnsavedChanges = force;
    console.log('Force unsaved changes:', force);
  }

  /**
   * For testing purposes: Check if force mode is enabled
   */
  isForceMode(): boolean {
    return this.forceUnsavedChanges;
  }
} 